package br.com.vvrefeicoes.portal.feature.purchases

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.PrimaryScrollableTabRow
import androidx.compose.material3.PrimaryTabRow
import androidx.compose.material3.Surface
import androidx.compose.material3.Tab
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.di.rememberSessionPermissions
import br.com.vvrefeicoes.portal.core.ui.components.AppBanner
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppFooter
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.layout.AppScrollColumn
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MoneyFormatter
import br.com.vvrefeicoes.portal.core.util.SystemDateTimeProvider
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.PermissionAction
import br.com.vvrefeicoes.portal.domain.model.PermissionKeys
import br.com.vvrefeicoes.portal.domain.model.PurchasesProvision
import br.com.vvrefeicoes.portal.domain.model.PurchasesSummaryLine
import br.com.vvrefeicoes.portal.domain.model.allows
import br.com.vvrefeicoes.portal.domain.model.allowsAny
import br.com.vvrefeicoes.portal.feature.operacional.MonthPeriodSelector
import br.com.vvrefeicoes.portal.navigation.PurchasesRoute
import com.composables.icons.lucide.Banknote
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.RefreshCw
import com.composables.icons.lucide.ShoppingCart
import com.composables.icons.lucide.Upload
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.compras_entries_title
import portalvv.shared.generated.resources.compras_import_sheet
import portalvv.shared.generated.resources.compras_import_success
import portalvv.shared.generated.resources.compras_items
import portalvv.shared.generated.resources.compras_no_data
import portalvv.shared.generated.resources.compras_open_panel
import portalvv.shared.generated.resources.compras_provision_button
import portalvv.shared.generated.resources.compras_provision_registered
import portalvv.shared.generated.resources.compras_refresh
import portalvv.shared.generated.resources.compras_summary_complete
import portalvv.shared.generated.resources.compras_summary_consolidated
import portalvv.shared.generated.resources.compras_summary_empty
import portalvv.shared.generated.resources.compras_tab_summary

private enum class PurchasesTab(
    val category: PurchasesCategory?,
) {
    Summary(null),
    Proteins(PurchasesCategory.Proteins),
    Staples(PurchasesCategory.Staples),
    Dairy(PurchasesCategory.Dairy),
    Produce(PurchasesCategory.Produce),
    Chinese(PurchasesCategory.Chinese),
    Cleaning(PurchasesCategory.Cleaning),
    Disposables(PurchasesCategory.Disposables),
    Extras(PurchasesCategory.Extras),
    ;

    val label: StringResource
        get() = category?.label ?: Res.string.compras_tab_summary

    companion object {
        fun fromCategory(category: PurchasesCategory): PurchasesTab = entries.first { it.category == category }
    }
}

@Composable
fun PurchasesEntriesScreen(onNavigate: (Any) -> Unit = {}) {
    val permissions = rememberSessionPermissions()
    val canImport = permissions.allows(PermissionKeys.PURCHASES_ENTRIES, PermissionAction.CREATE)
    val canEdit = permissions.allows(PermissionKeys.PURCHASES_ENTRIES, PermissionAction.EDIT)
    val canOpenPanel = permissions.allows(PermissionKeys.PURCHASES, PermissionAction.VIEW)
    val canManageReasons = permissions.allowsAny(PermissionKeys.PURCHASES_ENTRY_REASONS)
    val canViewReasons = permissions.allows(PermissionKeys.PURCHASES_ENTRY_REASONS, PermissionAction.VIEW)
    val canCreateReason = permissions.allows(PermissionKeys.PURCHASES_ENTRY_REASONS, PermissionAction.CREATE)
    val canEditReason = permissions.allows(PermissionKeys.PURCHASES_ENTRY_REASONS, PermissionAction.EDIT)
    val appGraph = LocalAppGraph.current
    val controller = remember(appGraph) { PurchasesEntriesController(appGraph.purchasesRepository) }
    val extraController = remember(appGraph) { PurchasesExtraOrdersController(appGraph.purchasesExtraOrderRepository) }
    val scope = rememberCoroutineScope()
    var selectedMonth by remember { mutableStateOf(SystemDateTimeProvider().currentPeriod()) }
    var selectedTab by remember { mutableStateOf(PurchasesTab.Summary) }
    var showImportDialog by remember { mutableStateOf(false) }
    var showProvisionDialog by remember { mutableStateOf(false) }
    var importSucceeded by remember { mutableStateOf(false) }
    var refreshToken by remember { mutableStateOf(0) }
    var refreshing by remember { mutableStateOf(false) }
    var loadedMonth by remember { mutableStateOf<MonthPeriod?>(null) }
    var monthInitialized by remember { mutableStateOf(false) }

    LaunchedEffect(Unit) {
        val latest = appGraph.purchasesRepository.latestPeriod()
        if (latest != null && latest != selectedMonth) {
            selectedMonth = latest
        }
        monthInitialized = true
    }

    LaunchedEffect(selectedMonth, refreshToken) {
        if (!monthInitialized) return@LaunchedEffect
        val period = selectedMonth
        val refreshingCurrentMonth = loadedMonth == period
        refreshing = refreshingCurrentMonth
        if (!refreshingCurrentMonth) {
            controller.clearRowsCache()
            extraController.clearCachedData()
        }
        controller.dismissError()
        extraController.dismissError()
        coroutineScope {
            val jobs =
                buildList {
                    add(async { controller.loadSummary(period) })
                    add(async { controller.loadProvision(period) })
                    add(async { extraController.loadSummary(period) })
                    add(async { extraController.load(period) })
                    add(async { extraController.loadUnits() })
                    add(async { extraController.loadReasons(includeInactive = canViewReasons) })
                    add(async { controller.loadAllRows(period) })
                }
            jobs.awaitAll()
        }
        loadedMonth = period
        refreshing = false
    }

    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val panelMinHeight = (maxHeight - 160.dp).coerceAtLeast(0.dp)

        AppScrollColumn(
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(AppSpacing.space24),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
            footer = { AppFooter() },
        ) {
            Text(
                text = stringResource(Res.string.compras_entries_title),
                style = MaterialTheme.typography.headlineMedium,
            )

            PurchasesPanel(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .let { panelModifier ->
                            if (selectedTab == PurchasesTab.Summary) {
                                panelModifier.height(panelMinHeight)
                            } else {
                                panelModifier.heightIn(min = panelMinHeight)
                            }
                        },
                fillContentHeight = selectedTab == PurchasesTab.Summary,
                canImport = canImport,
                month = selectedMonth,
                onMonthChange = { selectedMonth = it },
                onImport = {
                    importSucceeded = false
                    showImportDialog = true
                },
                onProvision = { showProvisionDialog = true },
                provision = controller.provision,
                importSucceeded = importSucceeded,
                onDismissImportSuccess = { importSucceeded = false },
                error = controller.error ?: extraController.error,
                onDismissError = {
                    controller.dismissError()
                    extraController.dismissError()
                },
                refreshing = refreshing,
                onRefresh = { refreshToken++ },
                selectedTab = selectedTab,
                onSelectTab = { selectedTab = it },
            ) {
                val category = selectedTab.category
                when {
                    selectedTab == PurchasesTab.Summary -> {
                        val categoryItems =
                            PurchasesCategory.entries.map { summaryCategory ->
                                PurchasesSummaryItem(
                                    title = summaryCategory.label,
                                    icon = summaryCategory.icon,
                                    iconBackground = summaryCategory.iconBackground,
                                    iconColor = summaryCategory.iconColor,
                                    value =
                                        if (summaryCategory == PurchasesCategory.Extras) {
                                            extraController.count
                                                .takeIf { it > 0 }
                                                ?.let { PurchasesSummaryValue.Items(it) }
                                                ?: PurchasesSummaryValue.None
                                        } else {
                                            summaryValue(summaryCategory, controller.summary)
                                        },
                                    onClick = { selectedTab = PurchasesTab.fromCategory(summaryCategory) },
                                )
                            }
                        if (categoryItems.none { it.value != PurchasesSummaryValue.None }) {
                            Box(modifier = Modifier.fillMaxSize()) {
                                EmptyState(message = stringResource(Res.string.compras_summary_empty))
                            }
                        } else {
                            val consolidatedCents =
                                PurchasesCategory.entries
                                    .filter { it != PurchasesCategory.Extras }
                                    .sumOf { summaryCategory ->
                                        controller.summary
                                            .firstOrNull { it.type == summaryCategory.type }
                                            ?.totalCents
                                            ?: 0L
                                    }
                            val consolidatedItem =
                                PurchasesSummaryItem(
                                    title = Res.string.compras_summary_consolidated,
                                    icon = Lucide.ShoppingCart,
                                    iconBackground = AppPalette.Primary100,
                                    iconColor = AppPalette.Primary900,
                                    value = PurchasesSummaryValue.Amount(consolidatedCents),
                                    highlighted = true,
                                )
                            val summaryScrollState = rememberScrollState()
                            Column(
                                modifier = Modifier.fillMaxSize(),
                                verticalArrangement = Arrangement.SpaceBetween,
                            ) {
                                Column(
                                    modifier =
                                        Modifier
                                            .fillMaxWidth()
                                            .weight(1f)
                                            .verticalScroll(summaryScrollState),
                                ) {
                                    PurchasesSummaryGrid(
                                        items = listOf(consolidatedItem) + categoryItems,
                                    )
                                }
                                if (categoryItems.all { it.value != PurchasesSummaryValue.None } && canOpenPanel) {
                                    Column(
                                        modifier =
                                            Modifier
                                                .fillMaxWidth()
                                                .padding(top = AppSpacing.space32),
                                        horizontalAlignment = Alignment.CenterHorizontally,
                                        verticalArrangement = Arrangement.spacedBy(AppSpacing.space8),
                                    ) {
                                        Text(
                                            text = stringResource(Res.string.compras_summary_complete),
                                            style = MaterialTheme.typography.bodyMedium,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                                        )
                                        AppButton(
                                            text = stringResource(Res.string.compras_open_panel),
                                            onClick = { onNavigate(PurchasesRoute) },
                                        )
                                    }
                                }
                            }
                        }
                    }
                    category != null && category != PurchasesCategory.Extras -> {
                        PurchasesCategoryContent(
                            category = category,
                            rows = category.type?.let(controller::rowsFor).orEmpty(),
                            totalCents =
                                controller.summary
                                    .firstOrNull { it.type == category.type }
                                    ?.totalCents
                                    ?: 0L,
                            loading = category.type?.let(controller::isLoading) == true,
                            canEdit = canEdit,
                            onSaveProduct = { updated ->
                                val type = category.type
                                if (type != null) {
                                    scope.launch {
                                        if (controller.updateProduct(updated) != null) {
                                            controller.loadRows(type, selectedMonth)
                                            controller.loadSummary(selectedMonth)
                                        }
                                    }
                                }
                            },
                        )
                    }
                    category == PurchasesCategory.Extras -> {
                        PurchasesExtrasContent(
                            period = selectedMonth,
                            orders = extraController.orders,
                            loading = extraController.loading,
                            canEdit = canEdit,
                            canAdd = canImport,
                            units = extraController.units,
                            reasons = extraController.reasons,
                            canManageReasons = canManageReasons,
                            canCreateReason = canCreateReason,
                            canEditReason = canEditReason,
                            reasonsLoading = extraController.reasonsLoading,
                            reasonError = extraController.reasonError,
                            onSaveOrder = { updated ->
                                scope.launch {
                                    if (extraController.update(updated) != null) {
                                        extraController.load(selectedMonth)
                                        extraController.loadSummary(selectedMonth)
                                    }
                                }
                            },
                            onCreateOrder = { order ->
                                scope.launch {
                                    if (extraController.create(selectedMonth, order) != null) {
                                        extraController.load(selectedMonth)
                                        extraController.loadSummary(selectedMonth)
                                    }
                                }
                            },
                            onDeleteOrder = { order ->
                                scope.launch {
                                    if (extraController.delete(order)) {
                                        extraController.load(selectedMonth)
                                        extraController.loadSummary(selectedMonth)
                                    }
                                }
                            },
                            onCreateReason = { name ->
                                scope.launch { extraController.createReason(name) }
                            },
                            onRenameReason = { id, name ->
                                scope.launch {
                                    if (extraController.renameReason(id, name)) {
                                        extraController.load(selectedMonth)
                                    }
                                }
                            },
                            onChangeReasonStatus = { id, active ->
                                scope.launch { extraController.changeReasonStatus(id, active) }
                            },
                            onDismissReasonError = extraController::dismissReasonError,
                        )
                    }
                    else -> {
                        PurchasesEmptyTab()
                    }
                }
            }
        }
    }

    if (showProvisionDialog) {
        PurchasesProvisionDialog(
            period = selectedMonth,
            provision = controller.provision,
            saving = controller.provisionSaving,
            onDismiss = { showProvisionDialog = false },
            onSave = { amountCents ->
                scope.launch {
                    if (controller.saveProvision(selectedMonth, amountCents)) {
                        showProvisionDialog = false
                    }
                }
            },
        )
    }

    if (showImportDialog) {
        PurchasesImportDialog(
            period = selectedMonth,
            initialCategory = selectedTab.category ?: PurchasesCategory.Proteins,
            controller = controller,
            extraController = extraController,
            onDismiss = { showImportDialog = false },
            onImported = {
                showImportDialog = false
                importSucceeded = true
                val type = selectedTab.category?.type
                scope.launch {
                    controller.loadSummary(selectedMonth)
                    extraController.loadSummary(selectedMonth)
                    if (type != null) controller.loadRows(type, selectedMonth)
                    if (selectedTab == PurchasesTab.Extras) extraController.load(selectedMonth)
                }
            },
        )
    }
}

@Composable
private fun PurchasesPanel(
    modifier: Modifier,
    fillContentHeight: Boolean = false,
    canImport: Boolean,
    month: MonthPeriod,
    onMonthChange: (MonthPeriod) -> Unit,
    onImport: () -> Unit,
    onProvision: () -> Unit,
    provision: PurchasesProvision?,
    importSucceeded: Boolean,
    onDismissImportSuccess: () -> Unit,
    error: String?,
    onDismissError: () -> Unit,
    refreshing: Boolean,
    onRefresh: () -> Unit,
    selectedTab: PurchasesTab,
    onSelectTab: (PurchasesTab) -> Unit,
    content: @Composable () -> Unit,
) {
    Surface(
        modifier = modifier,
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            PurchasesToolbar(
                canImport = canImport,
                month = month,
                onMonthChange = onMonthChange,
                onImport = onImport,
                onProvision = onProvision,
                provision = provision,
                refreshing = refreshing,
                onRefresh = onRefresh,
            )

            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)

            if (importSucceeded) {
                AppBanner(
                    text = stringResource(Res.string.compras_import_success),
                    onDismiss = onDismissImportSuccess,
                    modifier = Modifier.padding(horizontal = AppSpacing.space16, vertical = AppSpacing.space12),
                )
            }

            if (error != null) {
                AppBanner(
                    text = error,
                    onDismiss = onDismissError,
                    isError = true,
                    modifier = Modifier.padding(horizontal = AppSpacing.space16, vertical = AppSpacing.space12),
                )
            }

            PurchasesTabs(
                selectedTab = selectedTab,
                onSelect = onSelectTab,
            )

            Column(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .then(if (fillContentHeight) Modifier.weight(1f) else Modifier)
                        .padding(AppSpacing.space16),
            ) {
                content()
            }
        }
    }
}

@Composable
private fun PurchasesToolbar(
    canImport: Boolean,
    month: MonthPeriod,
    onMonthChange: (MonthPeriod) -> Unit,
    onImport: () -> Unit,
    onProvision: () -> Unit,
    provision: PurchasesProvision?,
    refreshing: Boolean,
    onRefresh: () -> Unit,
) {
    Row(
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(horizontal = AppSpacing.space16, vertical = AppSpacing.space12),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Column(
            horizontalAlignment = Alignment.Start,
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space8),
        ) {
            if (canImport) {
                Row {
                    AppButton(
                        text = stringResource(Res.string.compras_import_sheet),
                        onClick = onImport,
                        leadingIcon = Lucide.Upload,
                    )
                    Spacer(Modifier.size(AppSpacing.space8))
                    AppButton(
                        text = stringResource(Res.string.compras_provision_button),
                        onClick = onProvision,
                        variant = PortalButtonVariant.Outlined,
                        leadingIcon = Lucide.Banknote,
                    )
                }
            }
            if (provision?.configured == true) {
                Text(
                    text =
                        stringResource(
                            Res.string.compras_provision_registered,
                            "R$ ${MoneyFormatter.formatCents(provision.amountCents)}",
                        ),
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
        Spacer(Modifier.weight(1f))
        Column(
            horizontalAlignment = Alignment.End,
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space8),
        ) {
            MonthPeriodSelector(
                month = month,
                onMonthChange = onMonthChange,
            )
            AppButton(
                text = stringResource(Res.string.compras_refresh),
                onClick = onRefresh,
                enabled = !refreshing,
                variant = PortalButtonVariant.Outlined,
                leadingIcon = Lucide.RefreshCw,
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun PurchasesTabs(
    selectedTab: PurchasesTab,
    onSelect: (PurchasesTab) -> Unit,
) {
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        if (maxWidth >= 900.dp) {
            PrimaryTabRow(selectedTabIndex = PurchasesTab.entries.indexOf(selectedTab)) {
                PurchasesTab.entries.forEach { tab ->
                    Tab(
                        selected = tab == selectedTab,
                        onClick = { onSelect(tab) },
                        text = { Text(text = stringResource(tab.label)) },
                    )
                }
            }
        } else {
            PrimaryScrollableTabRow(selectedTabIndex = PurchasesTab.entries.indexOf(selectedTab)) {
                PurchasesTab.entries.forEach { tab ->
                    Tab(
                        selected = tab == selectedTab,
                        onClick = { onSelect(tab) },
                        text = { Text(text = stringResource(tab.label)) },
                    )
                }
            }
        }
    }
}

private fun summaryValue(
    category: PurchasesCategory,
    summary: List<PurchasesSummaryLine>,
): PurchasesSummaryValue {
    val type = category.type ?: return PurchasesSummaryValue.None
    val line = summary.firstOrNull { it.type == type } ?: return PurchasesSummaryValue.None
    return if (line.productCount > 0) {
        PurchasesSummaryValue.Amount(line.totalCents)
    } else {
        PurchasesSummaryValue.None
    }
}

@Composable
private fun PurchasesSummaryGrid(items: List<PurchasesSummaryItem>) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
    ) {
        items.chunked(3).forEach { rowItems ->
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16),
            ) {
                rowItems.forEach { item ->
                    PurchasesSummaryCard(
                        item = item,
                        modifier = Modifier.weight(1f),
                    )
                }
                repeat(3 - rowItems.size) {
                    Spacer(Modifier.weight(1f))
                }
            }
        }
    }
}

@Composable
private fun PurchasesSummaryCard(
    item: PurchasesSummaryItem,
    modifier: Modifier,
) {
    val value =
        when (val summary = item.value) {
            is PurchasesSummaryValue.Amount -> "R$ ${MoneyFormatter.formatCents(summary.cents)}"
            is PurchasesSummaryValue.Items -> stringResource(Res.string.compras_items, summary.items)
            PurchasesSummaryValue.None -> stringResource(Res.string.compras_no_data)
        }
    val containerColor =
        if (item.highlighted) {
            AppPalette.Accent100
        } else {
            MaterialTheme.colorScheme.surface
        }
    val primaryContentColor =
        if (item.highlighted) {
            AppPalette.Accent900
        } else {
            MaterialTheme.colorScheme.onSurface
        }
    val secondaryContentColor =
        if (item.highlighted) {
            AppPalette.Accent900.copy(alpha = 0.8f)
        } else {
            MaterialTheme.colorScheme.onSurfaceVariant
        }
    val iconContainerColor =
        if (item.highlighted) {
            AppPalette.Accent500
        } else {
            item.iconBackground
        }
    val iconTint =
        if (item.highlighted) {
            AppPalette.Accent900
        } else {
            item.iconColor
        }
    val content: @Composable () -> Unit = {
        Row(
            modifier = Modifier.padding(AppSpacing.space16),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        ) {
            Box(modifier = Modifier.size(48.dp), contentAlignment = Alignment.Center) {
                if (!item.highlighted) {
                    Surface(
                        modifier = Modifier.size(48.dp),
                        shape = AppShapes.medium,
                        color = iconContainerColor,
                    ) {
                        Box(contentAlignment = Alignment.Center) {
                            Icon(
                                imageVector = item.icon,
                                contentDescription = null,
                                tint = iconTint,
                                modifier = Modifier.size(24.dp),
                            )
                        }
                    }
                } else {
                    Icon(
                        imageVector = item.icon,
                        contentDescription = null,
                        tint = iconTint,
                        modifier = Modifier.size(32.dp),
                    )
                }
            }
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                Text(
                    text = stringResource(item.title),
                    style = MaterialTheme.typography.bodySmall,
                    color = secondaryContentColor,
                )
                Text(
                    text = value,
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.SemiBold,
                    color = primaryContentColor,
                )
            }
        }
    }
    val border = if (item.highlighted) null else BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    val onClick = item.onClick
    if (onClick != null) {
        Surface(
            onClick = onClick,
            modifier = modifier.heightIn(min = 80.dp),
            shape = AppShapes.large,
            color = containerColor,
            contentColor = primaryContentColor,
            border = border,
        ) {
            content()
        }
    } else {
        Surface(
            modifier = modifier.heightIn(min = 80.dp),
            shape = AppShapes.large,
            color = containerColor,
            contentColor = primaryContentColor,
            border = border,
        ) {
            content()
        }
    }
}

@Composable
private fun PurchasesEmptyTab() {
    Box(
        modifier =
            Modifier
                .fillMaxWidth()
                .heightIn(min = 200.dp),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = stringResource(Res.string.compras_no_data),
            style = MaterialTheme.typography.titleMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
    }
}
